import axios from 'axios';
import { Auth } from 'aws-amplify';

import { buildApiUrl } from './apiConfig';
import { ApiEnvelope, getApiErrorMessage, unwrapApiData } from './apiResponse';

export interface Avatar {
  emoji: string;
  color: string;
}

export interface TeacherProfile {
  phone?: string;
  office_location?: string;
  office_hours?: string;
  bio?: string;
  subjects?: string[];
  preferred_contact_method?: string;
}

export interface User {
  user_id: string;
  school_id: string;
  role: 'admin' | 'school_admin' | 'teacher' | 'student';
  name: string;
  email: string;
  avatar?: Avatar; // User's avatar with emoji and color
  teacher_ids?: string[]; // Array of teacher user_ids (only for students)
  teacher_profile?: TeacherProfile; // Additional profile info for teachers
  created_at: number;
  updated_at: number;
}

export interface CreateUserData {
  school_id: string;
  role: 'admin' | 'school_admin' | 'teacher' | 'student';
  name: string;
  email: string;
  password?: string;
}

export interface UpdateUserData {
  name?: string;
  role?: 'admin' | 'school_admin' | 'teacher' | 'student';
  avatar?: Avatar;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export interface ListUsersResponse {
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface ListUsersApiResponse {
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages?: number;
    totalPages?: number;
  };
}

interface CreateUserApiResponse {
  message: string;
  user: User;
  temporary_password?: string;
  temporaryPassword?: string;
}

export interface CreateUserResponse {
  message: string;
  user: User;
  temporaryPassword?: string;
}

export interface UpdateUserResponse {
  message: string;
  user: User;
}

/**
 * Get authentication token from AWS Amplify
 * NOTE: Cognito Authorizer requires ID Token, not Access Token
 */
const getAuthToken = async (): Promise<string> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken(); // Changed from getAccessToken() to getIdToken()
  } catch (error) {
    console.error('Error getting auth token:', error);
    return '';
  }
};

/**
 * Get authorization headers
 */
const getAuthHeaders = async () => ({
  'Content-Type': 'application/json',
  Authorization: `${await getAuthToken()}`,
});

const normalizeListUsersResponse = (data: ListUsersApiResponse): ListUsersResponse => ({
  users: data.users,
  pagination: {
    total: data.pagination.total,
    page: data.pagination.page,
    limit: data.pagination.limit,
    totalPages: data.pagination.totalPages ?? data.pagination.total_pages ?? 0,
  },
});

const normalizeCreateUserResponse = (data: CreateUserApiResponse): CreateUserResponse => ({
  message: data.message,
  user: data.user,
  temporaryPassword: data.temporaryPassword ?? data.temporary_password,
});

/**
 * List users with optional filters
 */
export const listUsers = async (params?: ListUsersParams): Promise<ListUsersResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.status) queryParams.append('status', params.status);

    const headers = await getAuthHeaders();
    const queryString = queryParams.toString();
    const response = await axios.get<ApiEnvelope<ListUsersApiResponse>>(
      buildApiUrl(`users${queryString ? `?${queryString}` : ''}`, 'v2'),
      { headers },
    );
    return normalizeListUsersResponse(unwrapApiData(response.data, 'Failed to list users'));
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to list users'));
  }
};

/**
 * Get a user by ID
 */
export const getUser = async (userId: string): Promise<User> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<ApiEnvelope<{ user: User }>>(
      buildApiUrl(`users/${userId}`, 'v2'),
      {
        headers,
      },
    );
    return unwrapApiData(response.data, 'Failed to get user').user;
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get user'));
  }
};

/**
 * Create a new user
 */
export const createUser = async (data: CreateUserData): Promise<CreateUserResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post<ApiEnvelope<CreateUserApiResponse>>(
      buildApiUrl('users', 'v2'),
      data,
      {
        headers,
      },
    );
    return normalizeCreateUserResponse(unwrapApiData(response.data, 'Failed to create user'));
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to create user'));
  }
};

/**
 * Update a user
 */
export const updateUser = async (
  userId: string,
  data: UpdateUserData,
): Promise<UpdateUserResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<ApiEnvelope<UpdateUserResponse>>(
      buildApiUrl(`users/${userId}`, 'v2'),
      data,
      {
        headers,
      },
    );
    return unwrapApiData(response.data, 'Failed to update user');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to update user'));
  }
};

/**
 * Assign teachers to a student
 */
export const assignTeachersToStudent = async (
  studentId: string,
  teacherIds: string[],
): Promise<{ message: string; student_id: string; teacher_ids: string[] }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<
      ApiEnvelope<{ message: string; student_id: string; teacher_ids: string[] }>
    >(buildApiUrl(`users/${studentId}/teachers`, 'v2'), { teacher_ids: teacherIds }, { headers });
    return unwrapApiData(response.data, 'Failed to assign teachers');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to assign teachers'));
  }
};

/**
 * Get teachers assigned to a student
 */
export const getStudentTeachers = async (
  studentId: string,
): Promise<{
  student_id: string;
  student_name: string;
  teachers: Array<{ user_id: string; name: string; email: string; school_id: string }>;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<
      ApiEnvelope<{
        student_id: string;
        student_name: string;
        teachers: Array<{ user_id: string; name: string; email: string; school_id: string }>;
      }>
    >(buildApiUrl(`users/${studentId}/teachers`, 'v2'), { headers });
    return unwrapApiData(response.data, 'Failed to get student teachers');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get student teachers'));
  }
};

/**
 * Get students assigned to a teacher
 */
export const getTeacherStudents = async (
  teacherId: string,
): Promise<{
  teacher_id: string;
  teacher_name: string;
  students: Array<{ user_id: string; name: string; email: string; school_id: string }>;
  total_students: number;
}> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get<
      ApiEnvelope<{
        teacher_id: string;
        teacher_name: string;
        students: Array<{ user_id: string; name: string; email: string; school_id: string }>;
        total_students: number;
      }>
    >(buildApiUrl(`teachers/${teacherId}/students`, 'v2'), { headers });
    return unwrapApiData(response.data, 'Failed to get teacher students');
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to get teacher students'));
  }
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    admin: 'Platform Admin',
    school_admin: 'School Admin',
    teacher: 'Teacher',
    student: 'Student',
  };
  return roleMap[role] || role;
};

/**
 * Get role color for badges
 */
export const getRoleColor = (
  role: string,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const colorMap: Record<string, any> = {
    admin: 'error',
    school_admin: 'primary',
    teacher: 'info',
    student: 'success',
  };
  return colorMap[role] || 'default';
};

/**
 * Update teacher profile
 */
export const updateTeacherProfile = async (
  userId: string,
  profileData: TeacherProfile,
): Promise<{ message: string; user: User }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put<
      ApiEnvelope<{ message: string; profile: TeacherProfile; user?: User }>
    >(buildApiUrl(`users/${userId}/profile`, 'v2'), profileData, { headers });
    const data = unwrapApiData(response.data, 'Failed to update teacher profile');
    return {
      message: data.message,
      user: data.user || ({ user_id: userId, teacher_profile: data.profile } as User),
    };
  } catch (error: any) {
    if (error.request && !error.response) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(getApiErrorMessage(error, 'Failed to update teacher profile'));
  }
};
